import {
  ACT_STATUSES,
  CONTRACT_KIND_LABELS,
  formatDate,
  formatUah,
  fullName,
  type ActStatus,
} from '@hr/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  AlertTriangleIcon,
  FileStackIcon,
  FileTextIcon,
  ListIcon,
  WandSparklesIcon,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { Combobox } from '@/components/combobox';
import { EmptyState, PageHeader } from '@/components/page';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { usePreview } from '@/features/preview/use-preview';
import { SignedCopy } from '@/features/signed/signed-copy';
import { useRegistry } from '@/features/registry/use-registry';
import { MonthPicker } from '@/features/time/month-picker';
import { ACT_STATUS_LABELS, uk } from '@/i18n/uk';
import { currentMonth, shiftMonth } from '@/lib/months';
import { isHr } from '@/lib/roles';
import { errorMessage, trpc } from '@/lib/trpc';

export const Route = createFileRoute('/_app/acts')({
  validateSearch: z.object({
    month: z
      .string()
      .regex(/^\d{4}-\d{2}$/)
      .optional()
      .catch(undefined),
    project: z.uuid().optional().catch(undefined),
  }),
  component: ActsPage,
});

const STATUS_STYLE: Record<ActStatus, string> = {
  draft: '',
  issued: 'border-sky-300 text-sky-800 dark:text-sky-200',
  signed: 'border-emerald-300 text-emerald-800 dark:text-emerald-200',
  cancelled: 'text-muted-foreground line-through',
};

function ActsPage() {
  const openPreview = usePreview();
  const t = uk.finance;
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { me } = Route.useRouteContext();
  const canWrite = isHr(me.role);
  const qc = useQueryClient();
  const registry = useRegistry();
  // За замовчуванням — попередній місяць: акти формують після його завершення.
  const month = search.month ?? shiftMonth(currentMonth(), -1);
  const setSearch = (patch: Partial<typeof search>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  const list = useQuery(trpc.acts.list.queryOptions({ month, projectId: search.project }));
  const [generating, setGenerating] = useState(false);
  const setStatus = useMutation(
    trpc.acts.setStatus.mutationOptions({
      onError: (e) => toast.error(errorMessage(e)),
      onSettled: () => qc.invalidateQueries(trpc.acts.pathFilter()),
    }),
  );
  const total = (list.data ?? [])
    .filter((a) => a.status !== 'cancelled')
    .reduce((s, a) => s + a.amount, 0);

  return (
    <>
      <PageHeader
        title={t.acts}
        actions={
          canWrite && (
            <Button onClick={() => setGenerating(true)}>
              <WandSparklesIcon /> {t.generate}
            </Button>
          )
        }
      />
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <MonthPicker value={month} onChange={(m) => setSearch({ month: m })} />
        <Combobox
          className="w-44"
          value={search.project}
          onChange={(v) => setSearch({ project: v ?? undefined })}
          options={(registry.data?.projects ?? []).map((p) => ({ value: p.id, label: p.code }))}
          placeholder={uk.registry.project}
          allowClear
        />
        {(list.data?.length ?? 0) > 0 && (
          <span className="ml-auto text-sm text-muted-foreground">
            {t.total}:{' '}
            <span className="font-medium text-foreground tabular-nums">{formatUah(total)}</span>
          </span>
        )}
      </div>
      {list.isPending ? (
        <Skeleton className="h-48" />
      ) : list.data?.length === 0 ? (
        <EmptyState icon={FileStackIcon} title={t.emptyActs} />
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left font-medium">{t.number}</th>
                <th className="px-3 py-2 text-left font-medium">{t.employee}</th>
                <th className="px-3 py-2 text-left font-medium">{t.contract}</th>
                <th className="px-3 py-2 text-right font-medium">{t.hours}</th>
                <th className="px-3 py-2 text-right font-medium">{t.quantity}</th>
                <th className="px-3 py-2 text-right font-medium">{t.amount}</th>
                <th className="px-3 py-2 text-left font-medium">{t.status}</th>
                <th className="w-24" />
              </tr>
            </thead>
            <tbody>
              {list.data?.map((a) => (
                <tr key={a.id} className="border-t">
                  <td className="px-3 py-2 font-medium whitespace-nowrap">
                    {a.number}
                    <div className="text-xs font-normal text-muted-foreground">
                      {formatDate(a.actDate)}
                    </div>
                  </td>
                  <td className="px-3 py-2">{fullName(a)}</td>
                  <td className="px-3 py-2">
                    {a.contractNumber}
                    <div className="text-xs text-muted-foreground">
                      {CONTRACT_KIND_LABELS[a.contractKind]} · {a.projectCode}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{a.hours}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{a.quantity}</td>
                  <td
                    className="px-3 py-2 text-right font-medium tabular-nums"
                    title={a.formula ?? undefined}
                  >
                    {formatUah(a.amount)}
                  </td>
                  <td className="px-3 py-2">
                    {canWrite && a.status !== 'cancelled' ? (
                      <Select
                        value={a.status}
                        onValueChange={(status) =>
                          setStatus.mutate({ id: a.id, status: status as ActStatus })
                        }
                      >
                        <SelectTrigger size="sm" className="w-36" aria-label={t.status}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ACT_STATUSES.map((s) => (
                            <SelectItem key={s} value={s}>
                              {ACT_STATUS_LABELS[s]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge variant="outline" className={STATUS_STYLE[a.status]}>
                        {ACT_STATUS_LABELS[a.status]}
                      </Badge>
                    )}
                  </td>
                  <td className="px-2 py-1 text-right whitespace-nowrap">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-8"
                      aria-label={t.document}
                      title={t.document}
                      onClick={() => openPreview({ url: `/api/acts/${a.id}/document.docx` })}
                    >
                      <FileTextIcon />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-8"
                      aria-label={t.annex}
                      title={t.annex}
                      onClick={() => openPreview({ url: `/api/acts/${a.id}/annex.docx` })}
                    >
                      <ListIcon />
                    </Button>
                    <SignedCopy kind="act" entityId={a.id} title={`Акт № ${a.number}`} compact />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {generating && (
        <GenerateDialog
          month={month}
          projectId={search.project}
          onClose={() => setGenerating(false)}
        />
      )}
    </>
  );
}

function GenerateDialog({
  month,
  projectId,
  onClose,
}: {
  month: string;
  projectId: string | undefined;
  onClose: () => void;
}) {
  const t = uk.finance;
  const qc = useQueryClient();
  const preview = useQuery(trpc.acts.preview.queryOptions({ month, projectId }));
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const generate = useMutation(
    trpc.acts.generate.mutationOptions({
      onSuccess: (acts) => {
        toast.success(t.generated(acts.length));
        onClose();
      },
      onError: (e) => toast.error(errorMessage(e)),
      onSettled: () => {
        void qc.invalidateQueries(trpc.acts.pathFilter());
        void qc.invalidateQueries(trpc.serviceReports.pathFilter());
      },
    }),
  );
  const rows = preview.data ?? [];
  const chosen = rows.filter((r) => !excluded.has(r.contractId)).map((r) => r.contractId);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t.generateTitle}</DialogTitle>
          <DialogDescription>{t.generateHint}</DialogDescription>
        </DialogHeader>
        {preview.isPending ? (
          <Skeleton className="h-32" />
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t.nothingToGenerate}</p>
        ) : (
          <div className="max-h-[60vh] overflow-y-auto rounded-md border">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-muted text-xs text-muted-foreground">
                <tr>
                  <th className="w-8" />
                  <th className="px-2 py-1.5 text-left font-medium">{t.employee}</th>
                  <th className="px-2 py-1.5 text-right font-medium">{t.reports}</th>
                  <th className="px-2 py-1.5 text-right font-medium">{t.hours}</th>
                  <th className="px-2 py-1.5 text-right font-medium">{t.quantity}</th>
                  <th className="px-2 py-1.5 text-right font-medium">{t.amount}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.contractId} className="border-t align-top">
                    <td className="px-2 py-1.5">
                      <Checkbox
                        checked={!excluded.has(r.contractId)}
                        aria-label={r.employeeName}
                        onCheckedChange={(v) =>
                          setExcluded((prev) => {
                            const next = new Set(prev);
                            if (v === true) next.delete(r.contractId);
                            else next.add(r.contractId);
                            return next;
                          })
                        }
                      />
                    </td>
                    <td className="px-2 py-1.5">
                      <div className="font-medium">{r.employeeName}</div>
                      <div className="text-xs text-muted-foreground">
                        {r.contractNumber} · {r.projectCode} · {r.formula}
                      </div>
                      {r.capped && <div className="text-xs text-amber-700">{t.capped}</div>}
                      {r.reconciliation.length > 0 && (
                        <details className="text-xs text-amber-700 dark:text-amber-300">
                          <summary className="flex cursor-pointer items-center gap-1">
                            <AlertTriangleIcon className="size-3" /> {t.reconciliation}:{' '}
                            {r.reconciliation.length}
                          </summary>
                          <ul>
                            {r.reconciliation.map((i) => (
                              <li key={i.date}>
                                {t.reconciliationIssue(
                                  formatDate(i.date),
                                  i.timesheetHours === null
                                    ? t.noTimesheet
                                    : String(i.timesheetHours),
                                  String(i.reportHours),
                                )}
                              </li>
                            ))}
                          </ul>
                        </details>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{r.reports}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{r.hours}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{r.quantity}</td>
                    <td className="px-2 py-1.5 text-right font-medium tabular-nums">
                      {formatUah(r.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {uk.common.cancel}
          </Button>
          <Button
            disabled={chosen.length === 0 || generate.isPending}
            onClick={() => generate.mutate({ month, projectId, contractIds: chosen })}
          >
            {t.generate} ({chosen.length})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
